import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { ApiError, api } from '@/lib/api';
import { Alert, Button, Card } from '@/components/ui';
import { AuthShell } from '@/layouts/AuthShell';

type State = 'checking' | 'verified' | 'failed';

/**
 * Lands from the verification link in the registration email.
 *
 * The verification runs once even under React's development double-render: the
 * token is single-use, so firing the request twice would report failure on the
 * second attempt and tell a member their good link was bad.
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [state, setState] = useState<State>(token ? 'checking' : 'failed');
  const [message, setMessage] = useState<string>(
    token ? '' : 'That link is missing its token. Some email clients break long links across lines.',
  );
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;

    api
      .get(`/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(() => setState('verified'))
      .catch((error: unknown) => {
        setState('failed');
        setMessage(
          error instanceof ApiError
            ? error.message
            : 'We could not verify that link. It may have expired.',
        );
      });
  }, [token]);

  if (state === 'checking') {
    return (
      <AuthShell title="Verifying your email" description="This will only take a moment.">
        <Card className="flex items-center justify-center gap-3 p-10 text-foreground-secondary">
          <Loader2 size={18} className="animate-spin" /> Checking your link…
        </Card>
      </AuthShell>
    );
  }

  if (state === 'verified') {
    return (
      <AuthShell title="Email verified" description="Your account is active.">
        <Card className="p-6 sm:p-8">
          <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-success-soft text-success">
            <CheckCircle2 size={22} />
          </span>
          <p className="mb-6 leading-relaxed text-foreground-secondary">
            Thank you. You can sign in now and complete your KYC, which is the step before you can
            apply for a loan.
          </p>
          <Link to="/login">
            <Button full size="lg">
              Sign in
            </Button>
          </Link>
        </Card>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="We could not verify that link" description="It may have expired or been used.">
      <Card className="p-7 sm:p-8">
        <Alert tone="warning" className="mb-6">
          <span className="flex gap-2">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            {message}
          </span>
        </Alert>
        <p className="mb-6 text-sm leading-relaxed text-foreground-secondary">
          Verification links last 24 hours and can be used once. Sign in and you will be offered a
          new one.
        </p>
        <Link to="/login">
          <Button full>Go to sign in</Button>
        </Link>
      </Card>
    </AuthShell>
  );
}
